import { AccountActions } from '@portal/actions';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedSelect from '@portal/components/AdvancedSelect/AdvancedSelect';
import { getRouteStackPath } from '@portal/config/routes';
import { useNavigation } from '@portal/hooks/useNavigation';
import useReduxState from '@portal/hooks/useReduxState';
import { translate } from '@portal/services/i18n';

import { ClockCircleOutlined, MailOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';

import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as MessageService from '~/services/message';

const STEPS = [
  { titleKey: 'STEP_1_TITLE', descKey: 'STEP_1_DESC' },
  { titleKey: 'STEP_2_TITLE', descKey: 'STEP_2_DESC' },
  { titleKey: 'STEP_3_TITLE', descKey: 'STEP_3_DESC' },
];

const IncomeTaxScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();

  const [dateIncomeTax, setDateIncomeTax] = useState<string>('');
  const [valueDate, setValueDate] = useState<string>('');
  const [recentAccount, setRecentAccount] = useState(false);
  const [data, setData] = useState<{ value: number; name: string }[]>([]);

  const {
    user: { me: loggedUser },
  } = useReduxState();

  const handleSubmit = () => {
    if (dateIncomeTax === '') {
      MessageService.error('INCOME_TAX.ERROR_INCOME_TAX');
      return;
    }

    dispatch(
      AccountActions.getIncomeReport(dateIncomeTax.toString(), (err) => {
        if (err) {
          MessageService.error('PAGES.MY_ACCOUNT.INCOME_TAX.ERROR');

          return;
        }

        MessageService.success(
          'PAGES.MY_ACCOUNT.INCOME_TAX.SUCCESS_INCOME_TAX'
        );
        navigate(getRouteStackPath('PROFILE', 'PROFILE_PAGE'));
      })
    );
  };

  useEffect(() => {
    const items: string[] = [];
    let accountYear = DateTime.fromISO(
      loggedUser?.account?.createdAt ?? ''
    ).year;
    const currentYear = DateTime.now().year;

    if (accountYear === currentYear) return setRecentAccount(true);

    for (accountYear; currentYear > accountYear; accountYear += 1) {
      items.push(accountYear.toString());
      setData(
        items.map((object, index) => ({
          value: index,
          name: object,
        }))
      );
    }
  }, [loggedUser?.account?.createdAt]);

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: true,
            title: translate('PAGES.MY_ACCOUNT.TABS.ACCOUNT'),
            url: getRouteStackPath('PROFILE', 'PROFILE_PAGE'),
          },
          {
            active: false,
            title: translate('PAGES.MY_ACCOUNT.TABS.INCOME_TAX'),
            url: getRouteStackPath('PROFILE', 'INCOME_TAX'),
          },
        ]}
      />
      <div className="incomeTax-page">
        <AdvancedPanelContentHeader
          title={translate('PAGES.MY_ACCOUNT.INCOME_TAX.TITLE')}
          description={translate('PAGES.MY_ACCOUNT.INCOME_TAX.SUBTITLE')}
          testId="income-tax-header"
          goBackAction={() => navigate.back()}
        />
        {recentAccount ? (
          <div className="incomeTax-page__unavailable">
            <ClockCircleOutlined className="incomeTax-page__unavailable-icon" />
            <ThemedText className="incomeTax-page__unavailable-text">
              {translate('PAGES.MY_ACCOUNT.INCOME_TAX.UNAVAILABLE')}
            </ThemedText>
          </div>
        ) : (
          <AdvancedCard testId="income-tax-card">
            <div className="incomeTax-page__columns">
              <div className="incomeTax-page__how-it-works">
                <ThemedText variant="h3" className="incomeTax-page__how-title">
                  {translate('PAGES.MY_ACCOUNT.INCOME_TAX.HOW_IT_WORKS')}
                </ThemedText>
                <div className="incomeTax-page__steps">
                  {STEPS.map((step, i) => (
                    <div key={i} className="incomeTax-page__step">
                      <ThemedText className="incomeTax-page__step-number">
                        {i + 1}
                      </ThemedText>
                      <div className="incomeTax-page__step-content">
                        <ThemedText
                          variant="h3"
                          className="incomeTax-page__step-title"
                        >
                          {translate(
                            `PAGES.MY_ACCOUNT.INCOME_TAX.${step.titleKey}`
                          )}
                        </ThemedText>
                        <ThemedText className="incomeTax-page__step-desc">
                          {translate(
                            `PAGES.MY_ACCOUNT.INCOME_TAX.${step.descKey}`
                          )}
                        </ThemedText>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="incomeTax-page__form">
                <AdvancedSelect
                  variant="outlined"
                  label={translate(
                    'PAGES.MY_ACCOUNT.INCOME_TAX.SELECT_YEAR_LABEL'
                  )}
                  onChange={(value) => {
                    setDateIncomeTax(data[value].name);
                    setValueDate(value);
                  }}
                  value={valueDate}
                  options={data}
                />
                <div className="incomeTax-page__email-info">
                  <MailOutlined className="incomeTax-page__email-icon" />
                  <ThemedText>
                    {translate('PAGES.MY_ACCOUNT.INCOME_TAX.EMAIL_INFO')}
                  </ThemedText>
                </div>
                <div className="incomeTax-page__button">
                  <AdvancedButton
                    text={translate(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.SUBMIT_LABEL'
                    )}
                    onClick={handleSubmit}
                  />
                </div>
              </div>
            </div>
          </AdvancedCard>
        )}
      </div>
    </>
  );
};

export default IncomeTaxScreen;
